import {assert} from 'Asserts';

import {LayoutPath} from '../DataLayoutTypes';

/**
 * Walk a nested object/array along a path of keys, returning the value at the end.
 *
 * Validates at each step that:
 * - The current node is a non-null object or array
 * - Array keys are numeric and in bounds
 * - Object keys are strings that exist as own properties
 */
export function getAtLayoutPath<T = unknown>(root: object, path: LayoutPath): T {
  let current: unknown = root;
  for (const key of path) {
    if (Array.isArray(current)) {
      assert(
        typeof key === 'number' && key >= 0 && key < current.length,
        'Invalid layout path: array index out of bounds',
        {path, key}
      );
      current = current[key];
    } else if (typeof current === 'object' && current != null) {
      assert(
        typeof key === 'string' && Object.hasOwn(current, key),
        'Invalid layout path: missing property',
        {
          path,
          key,
        }
      );
      current = (current as Record<string, unknown>)[key];
    } else {
      assert(false, 'Invalid layout path: unexpected non-object', {path, key});
    }
  }
  return current as T;
}

/**
 * Set a value at a nested path inside a mutable object (e.g. an immer draft).
 *
 * Validates the path up to the parent, then sets the value at the final key.
 */
export function setAtLayoutPath(draft: object, path: LayoutPath, value: unknown): void {
  assert(path.length > 0, 'Layout path must not be empty', {path});
  const parent = getAtLayoutPath<Record<string | number, unknown>>(draft, path.slice(0, -1));
  parent[path[path.length - 1]] = value;
}
